import { useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: convex _generated
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const ACTION_LABELS: Record<string, string> = {
  block_user: 'Block User',
  unblock_user: 'Unblock User',
  block_org: 'Block Organisation',
  unblock_org: 'Unblock Organisation',
  create_template: 'Create Template',
  update_template: 'Update Template',
  toggle_template: 'Toggle Template',
  assign_template: 'Assign Template',
  remove_template_assignment: 'Remove Assignment',
};

const ACTION_COLORS: Record<string, string> = {
  block_user: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400',
  unblock_user: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  block_org: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400',
  unblock_org: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  create_template: 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400',
  update_template: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  toggle_template: 'bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400',
  assign_template: 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400',
  remove_template_assignment: 'bg-zinc-50 text-zinc-600 dark:bg-zinc-500/10 dark:text-zinc-400',
};

export function AuditLogPage() {
  const [actionFilter, setActionFilter] = useState('');

  const auditLog = useQuery(api.superadmin.queries.getAuditLog, {
    action: actionFilter || undefined,
    limit: 200,
  });

  const isLoading = auditLog === undefined;

  const formatDateTime = (ts: number) =>
    new Date(ts).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const parseMetadata = (metadata?: string) => {
    if (!metadata) return null;
    try {
      return JSON.parse(metadata);
    } catch {
      return null;
    }
  };

  return (
    <div className="flex flex-col gap-0 p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold text-foreground">Audit Log</h1>
      </div>

      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-3 border-b border-border">
          <Select
            value={actionFilter || 'all'}
            onValueChange={v => setActionFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {Object.entries(ACTION_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center px-6 py-2 border-b border-border bg-muted/30 text-xs font-medium uppercase text-muted-foreground">
          <span className="w-44">Date</span>
          <span className="w-40">Admin</span>
          <span className="w-44">Action</span>
          <span className="w-28">Target Type</span>
          <span className="flex-1">Details</span>
        </div>

        <div className="overflow-auto max-h-[60vh]">
          {isLoading && (
            <>
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex items-center px-6 py-3 border-b border-border gap-4">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ))}
            </>
          )}
          {!isLoading && auditLog.length === 0 && (
            <div className="px-6 py-12 text-center text-muted-foreground">No audit log entries</div>
          )}
          {!isLoading && auditLog.map(entry => {
            const meta = parseMetadata(entry.metadata);
            const colorClass = ACTION_COLORS[entry.action] ?? '';
            return (
              <div key={entry._id} className="flex items-center px-6 py-3 border-b border-border hover:bg-muted/50 transition-colors">
                <span className="w-44 text-xs text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
                <span className="w-40 text-sm truncate">{entry.adminName ?? entry.adminEmail ?? '—'}</span>
                <span className="w-44">
                  <Badge className={`text-xs ${colorClass}`} variant="outline">
                    {ACTION_LABELS[entry.action] ?? entry.action}
                  </Badge>
                </span>
                <span className="w-28 text-xs capitalize">{entry.targetType}</span>
                <span className="flex-1 text-xs text-muted-foreground truncate">
                  {meta?.name ?? meta?.email ?? meta?.orgName ?? meta?.templateName ?? entry.targetId}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
